import { ApolloError, useMutation, useQuery, useSubscription } from '@apollo/client';
import { useQuizContext, useQuizSessionContext, useUserContext } from 'contexts';
import { useState } from 'react';
import { userSessionProgress } from 'graphql/subscriptions';
import { insertUserSessionAnswer, updateUserSessionAnswer } from 'graphql/mutations';
import { Spinner, useSnackbar } from 'components';

import { InsertUserSessionAnswer, InsertUserSessionAnswerVariables } from 'graphql/mutations/types/InsertUserSessionAnswer';
import { UpdateUserSessionAnswer, UpdateUserSessionAnswerVariables } from 'graphql/mutations/types/UpdateUserSessionAnswer';
import { QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { UserSessionProgress, UserSessionProgressVariables } from 'graphql/subscriptions/types/UserSessionProgress';
import { getUserSessionAnswer } from 'graphql/queries';
import { UserSessionAnswer, UserSessionAnswerVariables } from 'graphql/queries/types/UserSessionAnswer';
import { Question } from 'types';
import { SessionTypes } from 'consts';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Navigate } from 'react-router-dom';
import { LiveAnswer } from './live-answer';
import { useArrowNavigation, useSwipe } from 'hooks';

interface Props {
    quizQuestions: QuizQuestions;
}

/*
 * TODOs:
 * - DONE - keep submitted answer visible
 * - DONE - on current index changed check if answer already exists in the DB and show it if exists
 * - DONE - implement timer per session settings - limited time to answer question for LivePaperless type
 * - DONE  - move getUserSessionAnswer  query from LiveAnswer to this component in order to fetch answerSavedId on reload and force refetch of the query (hopefully it does it from graphQL cache) - ATM after submitting a multiple choice answer all items are deselected... on refresh they get selected again
 * - DONE - add possibility to move through questions (arrow keys and swipe)
 */
export const QuickAnswer = (props: Props) => {
    const { quiz } = useQuizContext();
    const user = useUserContext();
    const snackbar = useSnackbar();
    const session = useQuizSessionContext();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

    const onPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((cIndex) => --cIndex);
        }
    };

    const onNextQuestion = () => {
        if (currentQuestionIndex + 1 < props.quizQuestions.quiz_master_quizzes_by_pk!.questions.length) {
            setCurrentQuestionIndex((cIndex) => ++cIndex);
        }
    };

    useArrowNavigation(onPreviousQuestion, onNextQuestion);
    useSwipe({ left: onPreviousQuestion, right: onNextQuestion });

    const { data: sessionProgressData } = useSubscription<UserSessionProgress, UserSessionProgressVariables>(userSessionProgress, {
        variables: {
            sessionId: session?.session.id!,
            userId: session?.userSession?.userId!
        },
        skip: !session?.session.id || !session?.userSession?.userId
    });

    const currentSessionSlideIndex = sessionProgressData?.quiz_master_quiz_session_by_pk?.session_progress?.progressIndex ?? -1;
    const isSessionPaused = sessionProgressData?.quiz_master_quiz_session_by_pk?.session_progress?.isPaused ?? false;
    const isSessionEnded = !!sessionProgressData?.quiz_master_quiz_session_by_pk?.quiz_session_user_progresses.at(0)?.hasEnded;

    const onAnswerAdded = (data: InsertUserSessionAnswer) => {
        if (data.insert_quiz_master_quiz_session_user_answers?.returning) {
            snackbar.success('Submitted');
            refetchUserAnswers();
        }
    };
    const onAnswerModified = (data: UpdateUserSessionAnswer) => {
        if (data.update_quiz_master_quiz_session_user_answers_by_pk?.id) {
            snackbar.success('Submitted');
            refetchUserAnswers();
        }
    };

    const onMutationError = (error: ApolloError) => {
        console.error(error);
        snackbar.error('Failed to save answer. Please try again...');
    };

    const onQueryError = (error: ApolloError) => {
        console.error('Error fetching user answer. ', error);
        snackbar.error('Error fetching user answer.');
    };

    const {
        loading: loadingAnswer,
        data: usersAnswer,
        refetch: refetchUserAnswers
    } = useQuery<UserSessionAnswer, UserSessionAnswerVariables>(getUserSessionAnswer, {
        variables: {
            questionIndex: session?.session.type! === SessionTypes.LiveQuickAnswer ? currentSessionSlideIndex : currentQuestionIndex!,
            sessionId: session!.session.id,
            userId: user.id
        },
        skip: currentSessionSlideIndex < 0 || (currentQuestionIndex ?? -1) === -1,
        onError: onQueryError
    });
    const answerSavedId = usersAnswer?.quiz_master_quiz_session_user_answers.at(0)?.id;

    const [addUserSessionAnswer, { loading: insertLoading }] = useMutation<InsertUserSessionAnswer, InsertUserSessionAnswerVariables>(
        insertUserSessionAnswer,
        {
            onCompleted: onAnswerAdded,
            onError: onMutationError
        }
    );
    const [modifyUserSessionAnswer, { loading: updateLoading }] = useMutation<UpdateUserSessionAnswer, UpdateUserSessionAnswerVariables>(
        updateUserSessionAnswer,
        {
            onCompleted: onAnswerModified,
            onError: onMutationError
        }
    );

    const handleAnsweredQuestion = async (isAnswerCorrect: boolean, question: Question, answer?: string) => {
        if (answerSavedId) {
            await modifyUserSessionAnswer({
                variables: {
                    id: answerSavedId,
                    answer,
                    isCorrect: isAnswerCorrect,
                    sessionId: session?.session.id!,
                    questionIndex: currentQuestionIndex,
                    userId: user.id,
                    answeredAt: new Date().toISOString()
                }
            });
            return;
        }

        await addUserSessionAnswer({
            variables: {
                answer,
                isCorrect: isAnswerCorrect,
                sessionId: session?.session.id!,
                questionIndex: currentQuestionIndex,
                userId: user.id,
                answeredAt: new Date().toISOString()
            }
        });
    };

    const onApplyToAnswer = async () => {
        await addUserSessionAnswer({
            variables: {
                sessionId: session?.session.id!,
                userId: user.id,
                questionIndex: currentSessionSlideIndex,
                answeredAt: new Date().toISOString()
            }
        });
    };

    const onJumpToCurrentQuestion = () => {
        setCurrentQuestionIndex(currentSessionSlideIndex);
    };

    const loading = insertLoading || updateLoading || loadingAnswer;

    const canGetQuestion =
        currentQuestionIndex != null &&
        currentQuestionIndex > -1 &&
        props.quizQuestions.quiz_master_quizzes_by_pk!.questions.length > currentQuestionIndex;
    const currentQuestion = canGetQuestion ? props.quizQuestions.quiz_master_quizzes_by_pk!.questions[currentQuestionIndex] : undefined;

    const answerScene = {
        [SessionTypes.LivePaperless]: () => (
            <>
                {currentQuestionIndex !== currentSessionSlideIndex && (
                    <Button variant="contained" onClick={onJumpToCurrentQuestion}>
                        Jump To Current
                    </Button>
                )}
                <Stack direction="row" height="100%">
                    <Button className="slide-button left" onClick={onPreviousQuestion}>
                        <KeyboardArrowLeftIcon fontSize="large" />
                    </Button>
                    <LiveAnswer
                        question={currentQuestion!}
                        onAnswer={handleAnsweredQuestion}
                        questionIndex={currentQuestionIndex ?? -1}
                        savedAnswer={usersAnswer?.quiz_master_quiz_session_user_answers.at(0)}
                    />
                    <Button className="slide-button right" onClick={onNextQuestion}>
                        <KeyboardArrowRightIcon fontSize="large" />
                    </Button>
                </Stack>
            </>
        ),
        [SessionTypes.LiveQuickAnswer]: () => (
            <Stack direction="column" height="100%" sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    onClick={onApplyToAnswer}
                    disabled={loading || !!answerSavedId}
                    sx={{ height: '80%', width: '80%', borderRadius: '50%' }}
                >
                    Answer Now
                    <br />
                    {`${currentSessionSlideIndex + 1}`}
                </Button>
            </Stack>
        ),
        [SessionTypes.Distributed]: () => <Navigate to={'/'} />,
        [SessionTypes.LiveShow]: () => <Navigate to={'/'} />
    };

    const getUserPromptScreens = (firstLineText: string, secondLineText: string) => (
        <Stack spacing={2} className="full-height center-children">
            <Typography variant="h2" className="quiz-title" style={quiz.styles?.titlePageStyles?.title}>
                {props.quizQuestions.quiz_master_quizzes_by_pk?.name}
            </Typography>
            <Typography>{firstLineText}</Typography>
            <Typography>{secondLineText}</Typography>
        </Stack>
    );

    if (currentSessionSlideIndex < 0) {
        return getUserPromptScreens('Quiz has not started yet', 'Please wait for the adminstrator to start the quiz');
    }

    if (isSessionEnded) {
        return getUserPromptScreens('Quiz has ended', 'Thank you for participating');
    }

    if (isSessionPaused) {
        return getUserPromptScreens(
            'Quiz is paused',
            'Relax during the break and be sure to return when the adminstrator continues the quiz'
        );
    }

    if (!currentQuestion && !loading) {
        return (
            <>
                {currentQuestionIndex !== currentSessionSlideIndex && (
                    <Button variant="contained" onClick={onJumpToCurrentQuestion}>
                        Jump To Current
                    </Button>
                )}
                <Stack direction="row" height="100%">
                    <Button className="slide-button left" onClick={onPreviousQuestion}>
                        <KeyboardArrowLeftIcon fontSize="large" />
                    </Button>
                    <Typography variant="h2" style={quiz.styles?.titlePageStyles?.title}>
                        No more questions
                    </Typography>
                    <Button className="slide-button right" onClick={onNextQuestion}>
                        <KeyboardArrowRightIcon fontSize="large" />
                    </Button>
                </Stack>
            </>
        );
    }

    return (
        <Stack spacing={2} className="full-height">
            {!loading && answerScene[session?.session.type! as SessionTypes]()}
            {loading && <Spinner />}
        </Stack>
    );
};
